import { useState } from "react";
import { ChevronDown, ChevronUp, Moon, Sprout, Ruler, Thermometer, Calendar, Lightbulb } from "lucide-react";
import { Ortaggio } from "@/data/ortaggi";

interface CardOrtaggioProps {
  ortaggio: Ortaggio;
  zona: "nord" | "centro" | "sud";
}

const CardOrtaggio = ({ ortaggio, zona }: CardOrtaggioProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg">
      {/* Header Card - Clickable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-3xl">{ortaggio.icona}</span>
          <div className="text-left">
            <h3 className="font-semibold text-lg text-foreground">{ortaggio.nome}</h3>
            <p className="text-sm text-muted-foreground capitalize">
              {ortaggio.tipo} • {ortaggio.difficolta}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-gradient-to-br from-blue-900/20 to-purple-900/20 px-3 py-1 rounded-full border border-blue-800/30">
            <span className="text-lg">{ortaggio.lunaConsigliata.fase === 'crescente' ? '🌓' : '🌗'}</span>
            <span className="text-xs font-medium text-foreground capitalize">
              {ortaggio.lunaConsigliata.fase}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-6 animate-accordion-down">
          {/* Periodo di Semina */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Periodo di Semina</h4>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              {ortaggio.semina.semenzaio && (
                <div>
                  <p className="text-sm font-medium text-foreground">Semenzaio:</p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {ortaggio.semina.semenzaio[zona]}
                  </p>
                </div>
              )}
              {ortaggio.semina.campAperto && (
                <div>
                  <p className="text-sm font-medium text-foreground">Campo aperto:</p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {ortaggio.semina.campAperto[zona]}
                  </p>
                </div>
              )}
              {ortaggio.semina.trapianto && (
                <div>
                  <p className="text-sm font-medium text-foreground">Trapianto:</p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {ortaggio.semina.trapianto[zona]}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Fase Lunare */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Moon className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Fase Lunare Ottimale</h4>
            </div>
            <div className="bg-gradient-to-br from-blue-900/10 to-purple-900/10 rounded-lg p-4 border border-blue-800/20">
              <p className="text-sm font-medium text-foreground capitalize mb-1">
                Luna {ortaggio.lunaConsigliata.fase}
              </p>
              <p className="text-sm text-muted-foreground">
                {ortaggio.lunaConsigliata.motivo}
              </p>
            </div>
          </div>
          
          {/* Dettagli Semina */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Sprout className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Dettagli Semina</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Profondità</p>
                <p className="text-sm text-foreground">{ortaggio.profondita}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Distanza piante</p>
                <p className="text-sm text-foreground">{ortaggio.distanza.traPiante}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Distanza file</p>
                <p className="text-sm text-foreground">{ortaggio.distanza.traFile}</p>
              </div>
            </div>
          </div>
          
          {/* Temperatura */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Thermometer className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Temperatura</h4>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div>
                <p className="text-sm font-medium text-foreground">Germinazione:</p>
                <p className="text-sm text-muted-foreground">
                  Min {ortaggio.temperatura.germinazione.min}, Ottimale {ortaggio.temperatura.germinazione.ottimale}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Tempo germinazione:</p>
                <p className="text-sm text-muted-foreground">{ortaggio.tempoGerminazione}</p>
              </div>
            </div>
          </div>
          
          {/* Terreno */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Ruler className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Terreno</h4>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground">{ortaggio.terreno}</p>
            </div>
          </div>
          
          {/* Raccolta */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Raccolta</h4>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground capitalize">{ortaggio.raccolta}</p>
            </div>
          </div>
          
          {/* Consigli Pratici */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Consigli Pratici</h4>
            </div>
            <div className="bg-primary/5 rounded-lg p-4">
              <ul className="space-y-2">
                {ortaggio.consigli.map((consiglio, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>{consiglio}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardOrtaggio;
